import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardList, Palette, Truck, ArrowRight, HelpCircle } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import { api, absUrl } from "@/lib/api";

const steps = [
  { icon: ClipboardList, title: "Onsite Client Consultation", desc: "We meet you in your yard, listen to your vision and take stock of the growing conditions unique to your property." },
  { icon: Palette, title: "Custom Vision Design & Estimate", desc: "If desired, Bloomers can draft a design reflecting your landscaping goals. We then develop a competitive estimate for relevant services and installation." },
  { icon: Truck, title: "Hand-select Plants & Install", desc: "We hand-pick every plant, coordinate our trusted install partners, and bring your garden to life. We remain onsite during installation to ensure successful completion." },
];

const faqs = [
  {
    q: "What kind of landscaping do you do?",
    a: "Our goal is to achieve our clients' vision by implementing plantscapes that thrive in the Lake Norman area. This includes installing shrubs, trees, perennials and annuals in addition to boulders, river rock, mulch and lighting…all of which makes the design truly “pop”.",
  },
  {
    q: "Are you insured and licensed?",
    a: "Bloomers has all the necessary insurance and equipment for each project.",
  },
  {
    q: "Who will work on my landscape?",
    a: "Our team members have extensive experience — extensive installation experience and horticultural backgrounds, and we will all be familiar with your unique landscape needs.",
  },
];

export default function ServicesPage() {
  const [carUrl, setCarUrl] = useState(null);
  const [carReady, setCarReady] = useState(false);
  const [runKey, setRunKey] = useState(0);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setCarUrl(r.data?.hero_car_url || null))
      .catch(() => setCarUrl(null));
  }, []);

  useEffect(() => {
    if (!carUrl) return;
    setCarReady(false);
    const img = new Image();
    img.onload = () => {
      setCarReady(true);
      setRunKey((k) => k + 1);
    };
    img.onerror = () => setCarReady(false);
    img.src = absUrl(carUrl);
  }, [carUrl]);

  const showCar = Boolean(carUrl) && carReady;

  return (
    <div data-testid="services-page">
      <Seo
        title="Garden Design & Installation Services"
        description="Bloomers' 3-step yard and garden design process: onsite consultation, computer design capturing client vision, and hand-selected plant install. Serving Lake Norman, NC."
        path="/services"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Yard and Garden Design & Installation",
          provider: { "@type": "LocalBusiness", name: "Bloomers" },
          areaServed: "Lake Norman, NC",
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "3-Step Process",
            itemListElement: steps.map((s, i) => ({
              "@type": "Offer",
              position: i + 1,
              itemOffered: { "@type": "Service", name: s.title },
            })),
          },
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      {/* Compact Ruby car stage */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-6 sm:pt-8 pb-2">
          <div className="relative h-[300px] sm:h-[360px] lg:h-[420px] rounded-3xl overflow-visible">
            {showCar && (
              <div className="absolute inset-x-0 bottom-2 sm:bottom-3 flex justify-center pointer-events-none">
                <img
                  key={`${carUrl}-${runKey}`}
                  data-testid="services-ruby-car"
                  src={absUrl(carUrl)}
                  alt="Ruby the Bloomers car"
                  className="ruby-drive-compact w-auto max-h-[210px] sm:max-h-[255px] lg:max-h-[300px] max-w-full object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Headline + subhead */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 pt-2 pb-10 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "0px 0px 200px 0px" }}
          transition={{ duration: 0.6 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight"
        >
          Garden Design <span className="text-[#21C078]">and Installation</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "0px 0px 200px 0px" }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mt-6 text-xl sm:text-2xl font-bold text-[#1C1C1C]/80"
        >
          Our 3-Step Process Starts With Listening To You!
        </motion.p>
      </section>

      {/* Step cards — same as Home Core Services */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "0px 0px 200px 0px" }}
              transition={{ delay: i * 0.1 }}
              className="card p-8 relative overflow-hidden"
              data-testid={`service-step-${i + 1}`}
            >
              <div className="absolute top-4 right-6 text-3xl font-black text-[#21C078]/25 leading-none select-none uppercase tracking-tight">
                Step {i + 1}
              </div>
              <div className="w-14 h-14 grid place-items-center rounded-2xl bg-[#21C078]/10 text-[#21C078] mb-6">
                <s.icon className="w-7 h-7" />
              </div>
              <h3 className="mt-1 text-xl font-black">{s.title}</h3>
              <p className="mt-3 text-[#1C1C1C]/70 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 flex justify-center">
          <Link to="/contact" data-testid="services-cta" className="btn-pill-primary">
            Start your project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 pb-28" data-testid="services-faq">
        <div className="text-center mb-10">
          <span className="pill-tag bg-[#219DC0]/10 text-[#219DC0]">
            <HelpCircle className="w-3.5 h-3.5 mr-1.5" /> FAQ
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-black tracking-tight">
            Frequently Asked <span className="text-[#21C078]">Questions</span>
          </h2>
          <p className="mt-4 text-[#1C1C1C]/70">
            Please contact us if you cannot find an answer to your question.
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="card px-6 !border-b-0"
            >
              <AccordionTrigger className="text-left text-lg font-black hover:no-underline data-[state=open]:text-[#21C078]">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#1C1C1C]/75 text-base leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-8 text-center">
          <Link to="/contact" data-testid="services-faq-cta" className="btn-pill-ghost">
            Still have questions? Contact us <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
